"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { subscriptionApi, type Plan } from "@/lib/api";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { trackEvent } from "@/lib/gtag";

const FEATURE_LABELS: Record<string, string> = {
  in_app_chat: "In-App Chat",
  collaborations: "Collaborations",
};

function formatFeature(feat: unknown): string {
  if (typeof feat !== "string") return String((feat as { text?: string })?.text ?? "");
  return FEATURE_LABELS[feat] ?? feat;
}

const FAQS = [
  {
    q: "Is there a free trial?",
    a: "Yes. Every plan includes a 14-day free trial — no automation limits are reduced during the trial, and you can cancel any time before it ends.",
  },
  {
    q: "Can I change plans later?",
    a: "Yes. You can upgrade at any time from your dashboard and the remaining days on your current plan are credited toward the new one. Downgrading requires cancelling your current plan first.",
  },
  {
    q: "Do you offer annual billing?",
    a: "Yes. Paying annually saves 20% compared to monthly billing on every plan.",
  },
  {
    q: "What counts as a 'daily reply'?",
    a: "Each automated DM or comment reply AureliusCo sends on your behalf counts as one daily reply. The limit resets every 24 hours and scales with your plan.",
  },
];

export default function PricingClient() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annually">("monthly");
  const [isIndian, setIsIndian] = useState(false);
  const [conversionRate, setConversionRate] = useState<number>(83);

  useEffect(() => {
    fetch("https://api.frankfurter.dev/v1/latest?base=USD&symbols=INR")
      .then((res) => res.json())
      .then((data) => {
        if (data.rates && data.rates.INR) setConversionRate(data.rates.INR);
      })
      .catch((err) => console.error("Failed to fetch conversion rate", err));
  }, []);

  useEffect(() => {
    subscriptionApi.getPlans().then(({ data, error }) => {
      if (!error) setPlans((data || []).filter((p) => p.isActive && p.isPublic).sort((a, b) => a.displayOrder - b.displayOrder));
      setLoading(false);
    });
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productSchema = plans.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "AureliusCo",
    description: "AI Instagram DM and comment automation for creators and brands.",
    brand: { "@type": "Brand", name: "AureliusCo" },
    offers: plans.map((plan) => ({
      "@type": "Offer",
      name: `${plan.name.trim()} Plan`,
      price: plan.priceMonthly,
      priceCurrency: "USD",
      url: "https://www.aureliusco.in/pricing",
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: plan.priceMonthly,
        priceCurrency: "USD",
        billingDuration: "P1M",
      },
    })),
  } : null;

  return (
    <>
      <Navbar />
      <div style={{ background: "#0A0908", minHeight: "100vh", color: "#F5F0E8" }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {productSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
        />
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-8" aria-label="Pricing" style={{ paddingTop: "clamp(5rem, 10vw, 8rem)", paddingBottom: "clamp(4rem, 8vw, 6rem)" }}>
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.25em] mb-4 flex items-center justify-center gap-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
            <span className="h-px w-5 inline-block" style={{ background: "#C9A84C" }} />
            Pricing
            <span className="h-px w-5 inline-block" style={{ background: "#C9A84C" }} />
          </p>
          <h1 style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 400, lineHeight: 1.1, color: "#F5F0E8" }}>
            Simple, transparent pricing
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
            Automate Instagram DMs and comments 24/7, built on Instagram&apos;s official API. Every plan includes a 14-day free trial — no credit card surprises.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <div className="flex gap-px" style={{ border: "1px solid rgba(201,168,76,0.12)" }}>
            {(["monthly", "annually"] as const).map((cycle) => (
              <button
                key={cycle}
                onClick={() => setBillingCycle(cycle)}
                className="py-2 px-6 text-xs font-medium uppercase tracking-[0.1em] transition-all cursor-pointer"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  background: billingCycle === cycle ? "#C9A84C" : "transparent",
                  color: billingCycle === cycle ? "#0A0908" : "rgba(245,240,232,0.4)",
                }}
              >
                {cycle === "monthly" ? "Monthly" : (
                  <span>Annually <span style={{ color: billingCycle === "annually" ? "rgba(10,9,8,0.55)" : "rgba(74,222,128,0.7)", fontSize: "0.62rem" }}>−20%</span></span>
                )}
              </button>
            ))}
          </div>
          <div className="flex gap-px" style={{ border: "1px solid rgba(201,168,76,0.12)" }}>
            <button
              onClick={() => setIsIndian(true)}
              className="py-2 px-5 text-xs font-medium uppercase tracking-[0.1em] transition-all cursor-pointer flex items-center gap-1.5"
              style={{ fontFamily: "'Montserrat', sans-serif", background: isIndian ? "#C9A84C" : "transparent", color: isIndian ? "#0A0908" : "rgba(245,240,232,0.4)" }}
            >
              🇮🇳 INR
            </button>
            <button
              onClick={() => setIsIndian(false)}
              className="py-2 px-5 text-xs font-medium uppercase tracking-[0.1em] transition-all cursor-pointer flex items-center gap-1.5"
              style={{ fontFamily: "'Montserrat', sans-serif", background: !isIndian ? "#C9A84C" : "transparent", color: !isIndian ? "#0A0908" : "rgba(245,240,232,0.4)" }}
            >
              🌎 USD
            </button>
          </div>
        </div>

        {/* Plans */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "rgba(201,168,76,0.3)", borderTopColor: "#C9A84C" }} />
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
          >
            {plans.map((plan) => {
              const price = billingCycle === "monthly" ? plan.priceMonthly : plan.priceAnnually;
              const features = Array.isArray(plan.features) ? plan.features : [];
              const comingSoon = Array.isArray(plan.comingSoonFeatures) ? plan.comingSoonFeatures : [];

              return (
                <motion.div
                  key={plan.id}
                  variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } } }}
                  whileHover={{ y: plan.badge ? -6 : -4, boxShadow: plan.badge ? "0 20px 60px rgba(201,168,76,0.12)" : "0 12px 40px rgba(201,168,76,0.06)" }}
                  transition={{ type: "spring", stiffness: 280, damping: 22 }}
                  className="relative h-full p-8 flex flex-col"
                  style={{
                    border: plan.badge ? "1px solid rgba(201,168,76,0.45)" : "1px solid rgba(201,168,76,0.1)",
                    background: plan.badge ? "rgba(201,168,76,0.035)" : "transparent",
                  }}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-0.5 text-[10px] font-medium tracking-widest uppercase"
                      style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}>
                      {plan.badge}
                    </div>
                  )}

                  <p className="text-xs tracking-widest uppercase mb-1" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>{plan.name.trim()}</p>

                  <p className="text-[11px] mb-6 leading-relaxed" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                    {plan.maxAccounts === -1 ? "Unlimited" : plan.maxAccounts} {plan.maxAccounts === 1 ? "account" : "accounts"} · {plan.dailyReplies === -1 ? "Unlimited" : plan.dailyReplies} replies/day
                  </p>

                  <div className="mb-1 flex items-baseline gap-2">
                    <span style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "2.8rem", fontWeight: 300, color: "#F5F0E8", lineHeight: 1 }}>
                      {isIndian
                        ? `₹${price ? Math.round(Number(price) * conversionRate).toLocaleString() : "0"}`
                        : `$${price ? Number(price).toFixed(2) : "0"}`}
                    </span>
                    <span className="text-xs" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>
                      / {billingCycle === "monthly" ? "month" : "year"}
                    </span>
                  </div>

                  {billingCycle === "monthly" && plan.priceAnnually ? (
                    <p className="text-[10px] mb-6" style={{ color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
                      {isIndian
                        ? `₹${Math.round((Number(plan.priceAnnually) * conversionRate) / 12).toLocaleString()}/mo billed annually — save 20%`
                        : `$${(Number(plan.priceAnnually) / 12).toFixed(2)}/mo billed annually — save 20%`}
                    </p>
                  ) : (
                    <div className="mb-6" />
                  )}

                  <ul className="space-y-3 mb-8 flex-1">
                    {[
                      `${plan.maxAccounts === -1 ? "Unlimited" : plan.maxAccounts} Instagram ${plan.maxAccounts === 1 ? "account" : "accounts"}`,
                      `${plan.dailyReplies === -1 ? "Unlimited" : plan.dailyReplies} daily replies`,
                      plan.automationLimit === -1 ? "Unlimited automations" : `${plan.automationLimit} automations`,
                      ...features.map(formatFeature).filter(Boolean),
                    ].map((text, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                        <span className="mt-0.5 flex-shrink-0" style={{ color: "#C9A84C" }}>✓</span>
                        <span>{text}</span>
                      </li>
                    ))}
                    {comingSoon.map((feat, idx) => (
                      <li key={`soon-${idx}`} className="flex items-start gap-3 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                        <span className="mt-0.5 flex-shrink-0" style={{ opacity: 0.3 }}>✓</span>
                        <span>
                          {formatFeature(feat)}
                          <span className="ml-2 px-1.5 py-0.5 text-[8px] uppercase tracking-tighter" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(245,240,232,0.3)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            Soon
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    href="/login"
                    onClick={() => trackEvent("select_plan", { plan_id: plan.id, plan_name: plan.name.trim(), billing_cycle: billingCycle })}
                    className="block w-full py-3.5 text-xs text-center font-medium tracking-widest uppercase transition-all duration-300"
                    style={plan.badge
                      ? { background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }
                      : { border: "1px solid rgba(201,168,76,0.28)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Start Free Trial →
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center mb-10" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 400, color: "#F5F0E8" }}>
            Pricing questions
          </h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.q} className="p-6" style={{ border: "1px solid rgba(201,168,76,0.09)", background: "rgba(255,255,255,0.018)" }}>
                <h3 className="text-sm font-medium mb-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>{faq.q}</h3>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related pages */}
        <div className="max-w-3xl mx-auto mt-16 text-center text-xs" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
          See what&apos;s included in every plan on our{" "}
          <Link href="/features" className="underline" style={{ color: "rgba(201,168,76,0.8)" }}>features page</Link>
          {" "}or check supported platforms on{" "}
          <Link href="/integrations" className="underline" style={{ color: "rgba(201,168,76,0.8)" }}>integrations</Link>.
        </div>
      </main>
      </div>
      <Footer />
    </>
  );
}
